import React, { useRef, useState } from 'react';
import { IconCheck, IconEye, IconEyeOff, IconKey, IconPhone, IconTag, IconX } from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import type { Provider, ProviderType } from '../../../entities/provider';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { PHONE_MASK_OPTIONS } from '../../../shared/lib/phone';
import { logAction } from '../../../shared/lib/userLogger';
import { Button } from '../../../shared/ui/Button';
import { Input } from '../../../shared/ui/Input';
import { Select } from '../../../shared/ui/Select';
import { InputMask } from '@react-input/mask';
import { EMPTY_PROVIDER_FORM, TYPE_OPTIONS, type ProviderForm } from '../model/types';

interface ProviderModalProps {
  mode: 'add' | 'edit';
  initial?: Provider | null;
  onClose: () => void;
  onSaved: (updated?: Provider) => void;
}

const initDefaults = (mode: 'add' | 'edit', initial?: Provider | null): ProviderForm => {
  if (mode === 'edit' && initial) {
    return {
      name: initial.name,
      key: initial.key,
      type: initial.type,
      actual: initial.actual,
      data_phone: (initial.data?.phone ?? ''),
      data_sender: (initial.data?.sender ?? ''),
    };
  }
  return EMPTY_PROVIDER_FORM;
};

const ProviderModal: React.FC<ProviderModalProps> = ({ mode, initial, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showKey, setShowKey] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<ProviderForm>({ defaultValues: initDefaults(mode, initial) });

  const providerType = watch('type');

  const buildData = (values: ProviderForm): Record<string, string> => {
    if (values.type === 'P1SMS') {
      return { phone: values.data_phone.trim(), sender: values.data_sender.trim() };
    }
    return {};
  };

  const handleFormSubmit = handleSubmit(async values => {
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: values.name.trim(),
        key: values.key.trim(),
        type: values.type,
        actual: values.actual,
        data: buildData(values),
      };
      if (mode === 'add') {
        const record = await pb.collection('provides').create<Provider>(payload);
        logAction(`Created provider: ${values.name.trim()}`, 'ADD');
        onSaved(record);
      } else {
        const record = await pb.collection('provides').update<Provider>(initial!.id, payload);
        logAction(`Edited provider: ${values.name.trim()}`, 'EDIT');
        onSaved(record);
      }
      onClose();
    } catch (err) {
      setError(getPbErrorMessage(err));
    } finally {
      setSaving(false);
    }
  });

  return (
    <div className="mo open" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="cm cm-create" style={{ maxWidth: 480 }}>
        <div className="cm-top">
          <div className="cm-ti">{mode === 'add' ? 'Добавить провайдера' : 'Редактировать провайдера'}</div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={16} stroke={2} />
          </button>
        </div>
        <form className="cm-create-body" onSubmit={handleFormSubmit} style={{ gap: 14 }}>

          <div className="adm-field">
            <label className="adm-label">
              Тип <span className="adm-req">*</span>
            </label>
            <Controller
              control={control}
              name="type"
              rules={{ required: 'Выберите тип' }}
              render={({ field }) => (
                <Select
                  options={TYPE_OPTIONS}
                  value={field.value}
                  onChange={v => field.onChange(v as ProviderType)}
                />
              )}
            />
            {errors.type && <div className="form-field-error">{errors.type.message}</div>}
          </div>

          <div className="adm-field">
            <label className="adm-label">
              Название <span className="adm-req">*</span>
            </label>
            <div className="adm-input-wrap">
              <IconTag size={15} stroke={2} className="adm-input-icon" />
              <input
                className="adm-input"
                type="text"
                placeholder="Название провайдера"
                autoComplete="off"
                {...register('name', { required: 'Введите название' })}
              />
            </div>
            {errors.name && <div className="form-field-error">{errors.name.message}</div>}
          </div>

          <div className="adm-field">
            <label className="adm-label">
              Ключ <span className="adm-req">*</span>
            </label>
            <div className="adm-input-wrap">
              <IconKey size={15} stroke={2} className="adm-input-icon" />
              <Input
                className="adm-input adm-input-has-suffix"
                type={showKey ? 'text' : 'password'}
                placeholder="API ключ"
                autoComplete="off"
                {...register('key', { required: 'Введите ключ' })}
              />
              <span className="adm-input-suffix">
                <button
                  type="button"
                  className="adm-pwd-btn"
                  onClick={() => setShowKey(v => !v)}
                  title={showKey ? 'Скрыть' : 'Показать'}
                >
                  {showKey ? <IconEyeOff size={15} stroke={2} /> : <IconEye size={15} stroke={2} />}
                </button>
              </span>
            </div>
            {errors.key && <div className="form-field-error">{errors.key.message}</div>}
          </div>

          {/* P1SMS extra fields */}
          {providerType === 'P1SMS' && (
            <div className="adm-field">
              <label className="adm-label">
                Отправитель (sender) <span className="adm-req">*</span>
              </label>
              <div className="adm-input-wrap">
                <IconTag size={15} stroke={2} className="adm-input-icon" />
                <Input
                  className="adm-input"
                  type="text"
                  placeholder="Имя отправителя"
                  autoComplete="off"
                  {...register('data_sender', {
                    validate: v => providerType !== 'P1SMS' || v.trim().length > 0 || 'Введите отправителя',
                  })}
                />
              </div>
              {errors.data_sender && <div className="form-field-error">{errors.data_sender.message}</div>}
            </div>
          )}

          {providerType === 'P1SMS' && (
            <div className="adm-field">
              <label className="adm-label">Номер телефона (отправитель)</label>
              <Controller
                control={control}
                name="data_phone"
                render={({ field }) => (
                  <div className="adm-input-wrap">
                    <IconPhone size={15} stroke={2} className="adm-input-icon" />
                    <InputMask
                      className="adm-input"
                      mask={PHONE_MASK_OPTIONS.mask}
                      replacement={PHONE_MASK_OPTIONS.replacement}
                      placeholder="+7 (___) ___-__-__"
                      type="tel"
                      autoComplete="off"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </div>
                )}
              />
            </div>
          )}

          {error && <div className="adm-form-error">{error}</div>}
          <div className="adm-form-actions">
            <Button variant="adm-cancel" type="button" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="adm-save" type="submit" disabled={saving}>
              {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
              {mode === 'add' ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { ProviderModal };
